import '../../../../../plugins/hlsjs/hls.min-es5';
import ListenerManager from './listenerManager';
import MediaManager from './mediaManager';
import Youbora from '../../../../classes/youbora';
import displayError from '../../../../../shared/actions/player/displayError';
import { IPlayerError } from '../../../../../shared/types/interfaces/error';
import { MediaPlayerProps as Props } from '../../../../../shared/types/props/player';
import { adsSelector } from '@shared/store/slices/ads.slice';
import { mediaSelector } from '@shared/store/slices/media.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  PlayerError,
  PlayerType,
} from '../../../../../shared/types/enums/player';

const ZAPPER_DELAY = 500;

export default function MediaPlayer({
  asset,
  channel,
  drm,
  headers,
  position,
  source,
}: Props) {
  const { pauseAd } = useAppSelector(adsSelector);
  const { startover: rawStartover } = useAppSelector(mediaSelector);
  const { params } = useAppSelector(routerSelector);
  const [loaded, setLoaded] = useState(false);
  const player = useRef<Hls | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  const youbora = useRef<Youbora | null>(null);
  const autoplay = useMemo(() => !pauseAd, []);
  const startover = useMemo(
    () => rawStartover || params.current?.restart || false,
    [params, rawStartover]
  );
  const props = {
    hls: player?.current,
    video: video?.current,
    source,
    ...position,
  };

  useEffect(() => {
    // Player and Youbora destruction
    return () => {
      player.current?.destroy();
      player.current = null;
      youbora.current?.destroy();
      youbora.current = null;
    };
  }, []);

  useEffect(() => {
    if (player.current) {
      youbora.current = new Youbora({
        asset,
        channel,
        player: { instance: player.current, type: PlayerType.DASHJS },
      });
      youbora.current?.start();
    }
    return () => {
      youbora.current?.destroy();
      youbora.current = null;
    };
  }, [startover]);

  useEffect(() => {
    // Initialize default error
    const error: IPlayerError = {
      player: PlayerType.HLSJS,
      type: PlayerError.LOAD_ERROR,
    };

    // Check if hls is available
    if (!Hls.isSupported()) {
      const text = 'Hls is not available';
      error.detail = text;
      console.error('[Hls.js]', text);
      displayError(error);
      return;
    }

    // Destroy previous youbora instance
    youbora.current?.destroy();
    youbora.current = null;

    let playerTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

    try {
      setLoaded(false);
      playerTimeout = setTimeout(() => {
        if (source) {
          // Required headers
          const {
            accessToken,
            contentType,
            deviceId,
            planId,
            platform,
            userId,
          } = headers || {};

          // Player configuration
          const config = {
            autoplay,
            liveDurationInfinity: true,
            startPosition:
              Math.max(position.keepWatching ?? 0, position.bookmark ?? 0) ||
              position.start,
            ...(drm && {
              emeEnabled: true,
              licenseXhrSetup: (xhr: XMLHttpRequest) => {
                xhr.setRequestHeader('content-type', contentType || '');
                xhr.setRequestHeader('x-access-token', accessToken || '');
                xhr.setRequestHeader('x-tvup-device', deviceId || '');
                xhr.setRequestHeader('x-tvup-planid', planId || '');
                xhr.setRequestHeader('x-tvup-platform', platform || '');
                xhr.setRequestHeader('x-tvup-userid', userId || '');
              },
              widevineLicenseUrl: drm?.license,
            }),
          };

          // Player creation
          player.current = new Hls(config);

          // Player and Youbora initialization
          if (video.current) {
            player.current.loadSource(source);
            const callback = () => {
              player.current.attachMedia(video.current);
            };
            if (!youbora.current) {
              youbora.current = new Youbora({
                player: { instance: player.current, type: PlayerType.HLSJS },
                asset,
                channel,
                callback,
              });
            } else {
              callback();
            }
            setLoaded(true);
          }
        } else {
          const text = 'No source available';
          error.detail = text;
          console.error('[Hls.js]', text);
          displayError(error);
        }
      }, ZAPPER_DELAY);
    } catch (e) {
      const text = 'Error loading player';
      error.detail = text;
      console.error('[Hls.js]', text, e);
      displayError(error);
    }

    return () => {
      player.current?.loadSource('');
      clearTimeout(playerTimeout);
      setLoaded(false);
    };
  }, [source]);

  return (
    <>
      <video
        autoPlay={autoplay}
        id={'video'}
        ref={video}
        height={'100%'}
        width={'100%'}
      />
      {loaded ? (
        <>
          <ListenerManager {...props} />
          <MediaManager {...props} />
        </>
      ) : null}
    </>
  );
}
